import type {
  Control,
  ControllerProps,
  FieldPath,
  FieldValues,
} from "react-hook-form";
import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";
import DOMPurify from "dompurify";
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { FormTooltip } from "@/components/ui/form-description";

interface CheckboxFieldProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> extends React.HTMLAttributes<HTMLDivElement> {
  control: Control<TFieldValues>;
  name: TName;
  label: string;
  description?: string;
  descriptionAsTooltip?: boolean;
  required?: boolean;
  requiredLabel?: string;
  controllerDefaultValue?: ControllerProps<TFieldValues, TName>["defaultValue"];
  shouldUnregister?: boolean;
}

export function CheckboxField<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>({
  control,
  name,
  label,
  description,
  descriptionAsTooltip = false,
  required = false,
  requiredLabel = "Required",
  controllerDefaultValue,
  shouldUnregister,
  className,
  ...htmlProps
}: CheckboxFieldProps<TFieldValues, TName>) {
  return (
    <FormField
      control={control}
      name={name}
      defaultValue={controllerDefaultValue}
      shouldUnregister={shouldUnregister}
      render={({ field }) => (
        <FormItem
          className={cn("flex flex-row items-start gap-3 space-y-0", className)}
          {...htmlProps}
        >
          <FormControl>
            <Checkbox
              ref={field.ref}
              name={field.name}
              checked={field.value}
              onCheckedChange={field.onChange}
              onBlur={field.onBlur}
            />
          </FormControl>
          <div className="space-y-1 leading-none">
            <FormLabel className="inline-block cursor-pointer leading-snug">
              <span
                className="[&_a]:text-primary [&_a]:underline [&_a]:underline-offset-4"
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize ? DOMPurify.sanitize(label) : "",
                }}
              />
              {required && (
                <>
                  <span className="ms-1 text-red-500" aria-hidden="true">
                    *
                  </span>
                  <span className="sr-only">({requiredLabel})</span>
                </>
              )}
            </FormLabel>
            {description && !descriptionAsTooltip && (
              <FormDescription>{description}</FormDescription>
            )}
            {description && descriptionAsTooltip && (
              <FormTooltip description={description} />
            )}
            <FormMessage />
          </div>
        </FormItem>
      )}
    />
  );
}
